import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import HotelNetRevenueSkeleton from 'lumen/components/hotel/net-revenue/skeleton';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Kpi } from 'lumen/utils/net-revenue/types';
import { deltaColorClass } from 'lumen/utils/net-revenue/variance';

interface HotelNetRevenueKpiCardSignature {
  Args: {
    dataTestId: string;
    isLoading?: boolean;
    kpi: Kpi | undefined;
    title: string;
  };
}

export default class HotelNetRevenueKpiCard extends Component<HotelNetRevenueKpiCardSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @service declare intl: Services['intl'];

  get changeColorClass(): string {
    const { kpi } = this.args;
    if (!kpi || kpi.changePercent === null) {
      return '';
    }
    // 0.0005 becomes 0.05%, and rounds to "0.0%" at display precision → neutral.
    return deltaColorClass(kpi.changePercent, {
      invert: kpi.invertColors,
      neutralThreshold: 0.0005,
    });
  }

  get formattedChangePercent(): string | null {
    const { kpi } = this.args;
    if (!kpi || kpi.changePercent === null) {
      return null;
    }
    return this.intl.formatNumber(kpi.changePercent, {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
      signDisplay: 'exceptZero',
    });
  }

  get formattedValue(): string {
    if (!this.args.kpi) {
      return this.intl.t('general.missing');
    }
    return this.intl.formatNumber(this.args.kpi.value, {
      style: 'currency',
      currency: this.hotelAndMarketService.hotel.currencyCode,
      currencyDisplay: 'symbol',
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    });
  }

  get isVarianceUnavailable(): boolean {
    return !!this.args.kpi && this.args.kpi.changePercent === null;
  }

  get subtitle(): string | null {
    const { kpi } = this.args;
    if (!kpi) {
      return null;
    }
    const pct = kpi.percentOfRevenue;
    if (pct != null) {
      const formattedPct = this.intl.formatNumber(pct, {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
      return this.intl.t('net_revenue.percent_of_room_revenue', {
        percent: formattedPct,
      });
    }
    return kpi.label ?? null;
  }

  <template>
    <PrismCard data-test-id={{@dataTestId}}>
      <:header as |Header|>
        <Header.title>{{@title}}</Header.title>
      </:header>

      <:content>
        {{#if @isLoading}}
          <div class="flex flex-col gap-200" data-test-id="kpi-card-skeleton">
            <HotelNetRevenueSkeleton class="rounded-100 h-200 w-3/4" />
            <HotelNetRevenueSkeleton class="rounded-100 h-200 w-1/2" />
          </div>
        {{else}}
          <div class="flex flex-col gap-100">
            <div class="flex items-center gap-100">
              <span
                class="text-400--bold truncate"
                data-test-id="kpi-card-value"
              >
                {{this.formattedValue}}
              </span>
              {{#if this.formattedChangePercent}}
                <span
                  class="text-200--regular {{this.changeColorClass}}"
                  data-test-id="kpi-card-change"
                >
                  {{this.formattedChangePercent}}
                </span>
              {{else if this.isVarianceUnavailable}}
                <span
                  class="text-200--regular text-neutral-subdued"
                  data-test-id="kpi-card-change"
                >
                  {{t "general.missing"}}
                </span>
              {{/if}}
            </div>
            {{#if this.subtitle}}
              <span
                class="text-200--regular text-neutral-subdued"
                data-test-id="kpi-card-subtitle"
              >
                {{this.subtitle}}
              </span>
            {{/if}}
          </div>
        {{/if}}
      </:content>
    </PrismCard>
  </template>
}
